import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import type { DealCockpitData, CockpitMilestone, CockpitParty } from '../../types';
import { Badge, Button, Card } from '../ui';
import NudgeComposer from './NudgeComposer';
import AskAiPanel from './AskAiPanel';
import CallInGhl, { UNREAD } from '../CallInGhl';
import { deadlineDate, pacificWallMs } from '../../utils/deadlineDates';

/**
 * The deal Overview's working half (OO3, 2026-09-03), built to his Claude Design artboard
 * (docs/planning/ui-update-claude-design-spec.md, 3c): four KPI cards in Oswald, the escrow
 * timeline card with named milestones and the "Next" pill, then Parties beside Money and the
 * Required documents checklist. The page header above carries the name, the pills and the
 * actions, so the cockpit no longer repeats them. Every handler is the one that shipped:
 * money edits, task toggles, the nudge composer, Ask AI.
 */

const fmtMoney = (v: number | null) =>
  v == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
const fmtDate = (d: string | null) => deadlineDate(d) ?? '';
const toUTC = (iso: string) => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);

const ROLE_LABEL: Record<string, string> = {
  escrow_officer: 'Escrow', title_officer: 'Title', lender: 'Lender', buyer: 'Buyer', seller: 'Seller',
  buyers_broker: "Buyer's broker", listing_broker: 'Listing broker', co_broker: 'Co-broker',
  attorney_buyer: "Buyer's attorney", attorney_seller: "Seller's attorney", appraiser: 'Appraiser',
  inspector: 'Inspector', property_manager: 'Property mgr', tenant: 'Tenant', other: 'Other',
};
const PARTY_ORDER = ['buyer', 'seller', 'lender', 'escrow_officer', 'title_officer', 'buyers_broker', 'listing_broker'];
/**
 * PHONE1 (F1): a party row is keyed by the PERSON, never the position - a reload (the Ask AI panel adds a party, then
 * re-sorts) can reorder the list, and an index key would hand one person's row (and the Call page just added on it) to
 * whoever sits there next. The role is in the key because one contact can hold two roles on a deal (deal_contacts is
 * unique on deal, contact AND role).
 */
const partyKey = (p: CockpitParty, i: number) => (p.contact_id ? `${p.contact_id}-${p.role}` : `${p.role}-${i}`);
/** Milestone dots: done is Apex black, the live one KW red with its ring, overdue red, ahead hollow. */
const NODE_CLS: Record<string, string> = {
  done: 'bg-gray-900',
  current: 'bg-primary-500 shadow-[0_0_0_4px_rgba(206,1,31,.16)]',
  overdue: 'bg-primary-500 shadow-[0_0_0_4px_rgba(206,1,31,.16)]',
  upcoming: 'bg-white border-2 border-gray-300',
};
const contingencyVariant = (s: string): 'success' | 'error' | 'warning' =>
  s === 'removed' ? 'success' : s === 'overdue' ? 'error' : 'warning';

const initialsOf = (name: string | null) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

function buildTimeline(anchor: string | null, coe: string | null, ms: CockpitMilestone[], nowMs: number) {
  if (!anchor || !coe) return null;
  const a = toUTC(anchor), c = toUTC(coe), span = c - a;
  if (!(span > 0)) return null;
  const pct = (m: number) => Math.max(0, Math.min(100, ((m - a) / span) * 100));
  const nextId = ms.find((m) => m.status === 'current')?.label ?? ms.find((m) => !m.is_completed)?.label ?? null;
  const nodes = ms.filter((m) => m.due_date).map((m) => ({ label: m.label, date: m.due_date!, pct: pct(toUTC(m.due_date!)), status: m.status, next: m.label === nextId }));
  // ESC1-MARK: the marker stands on the PACIFIC day - the axis is UTC-midnight dates, Date.now() is tomorrow from 5 PM PT.
  return { today: pct(pacificWallMs(nowMs)), nodes };
}

const h2 = 'text-[15px] font-semibold leading-none text-gray-900';
const eyebrow = 'text-[9.5px] font-bold uppercase leading-none tracking-[.13em] text-gray-400';
const linkCls = 'text-[11.5px] font-semibold leading-none text-primary-500 hover:text-primary-600 hover:underline underline-offset-[3px]';
const iconBtn = 'flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:border-gray-900 disabled:opacity-40 disabled:hover:border-gray-200';

function Kpi({ label, value, sub, valueClass = '', bar }: { label: string; value: string; sub?: string; valueClass?: string; bar?: number }) {
  return (
    <Card className="px-[18px] py-4">
      <div className={`${eyebrow} mb-[9px]`}>{label}</div>
      <div className={`font-display text-[26px] font-semibold leading-none tabular-nums text-gray-900 ${valueClass}`}>{value}</div>
      {sub && <div className="mt-[7px] text-[11.5px] leading-snug text-gray-500">{sub}</div>}
      {bar != null && (
        <div className="mt-[11px] h-1 overflow-hidden rounded-full bg-gray-150">
          <span className="block h-1 bg-gray-900" style={{ width: `${Math.max(0, Math.min(100, bar))}%` }} />
        </div>
      )}
    </Card>
  );
}

/**
 * OO3b: `onPropertyPhoto` hands the deal page the property photo from THIS payload, because the
 * header renders from `deal` alone and has no property of its own. It is called with the url or
 * with null, every load, so a deal whose property loses its photo clears the header thumb too.
 */
export default function DealCockpit({ dealId, onPropertyPhoto }: { dealId: string; onShare?: () => void; onPropertyPhoto?: (url: string | null) => void }) {
  const navigate = useNavigate();
  const [c, setC] = useState<DealCockpitData | null>(null);
  const [editMoney, setEditMoney] = useState(false);
  const [emd, setEmd] = useState('');
  const [loan, setLoan] = useState('');
  const [nudge, setNudge] = useState<{ party: CockpitParty; channel: 'sms' | 'email' } | null>(null);
  const [askAi, setAskAi] = useState(false);
  const [tasks, setTasks] = useState<DealCockpitData['loose_tasks']>([]);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  // Held in a ref, NOT in `load`'s deps: a caller passing an inline arrow would change identity
  // every render, changing `load`, re-firing the effect that calls it - a reload loop.
  const onPhotoRef = useRef(onPropertyPhoto);
  onPhotoRef.current = onPropertyPhoto;

  const load = useCallback(async () => {
    try { const d = (await api.getDealCockpit(dealId)).cockpit; setC(d); setTasks(d.loose_tasks || []); onPhotoRef.current?.(d.property?.photo_url || null); } catch { /* surfaced by the deal page */ }
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  const saveMoney = async () => {
    await api.updateCockpitMoney(dealId, { emd_amount: emd === '' ? null : Number(emd), loan_amount: loan === '' ? null : Number(loan) });
    setEditMoney(false); load();
  };
  const toggleTask = async (t: { id: string; is_completed: boolean }) => {
    setTasks((ts) => ts.map((x) => x.id === t.id ? { ...x, is_completed: !x.is_completed } : x));
    try { await api.updateDealTask(dealId, t.id, { is_completed: !t.is_completed }); } finally { load(); }
  };
  const addTask = async () => {
    if (!newTitle.trim()) return;
    const title = newTitle.trim(); setNewTitle(''); setAdding(false);
    await api.createDealTask(dealId, { title }); load();
  };

  if (!c) return null;
  const sortedParties = [...c.parties].sort((a, b) => {
    const ia = PARTY_ORDER.indexOf(a.role), ib = PARTY_ORDER.indexOf(b.role);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
  const nextM = c.milestones.find((m) => m.status === 'current') || c.milestones.find((m) => !m.is_completed) || null;
  const tl = buildTimeline(c.deal.escrow_opened_date, c.coe_date, c.milestones, Date.now());
  const na = c.next_action;
  const days = c.kpis.days_to_close;
  const presentDocs = c.documents.required.filter((d) => d.present).length;

  return (
    <div className="space-y-4">
      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
        <Kpi label="Purchase price" value={fmtMoney(c.kpis.price)} sub={c.money.emd != null ? `EMD ${fmtMoney(c.money.emd)}` : undefined} />
        <Kpi label="Your fee" value={fmtMoney(c.kpis.your_fee)} sub={c.money.gross_commission != null ? `of ${fmtMoney(c.money.gross_commission)} gross` : undefined} />
        <Kpi
          label="Close of escrow"
          value={days == null ? '—' : days < 0 ? `${Math.abs(days)}d past` : days === 0 ? 'Today' : `${days}d`}
          valueClass={days != null && days < 0 ? 'text-primary-500' : ''}
          sub={c.coe_date ? `Scheduled ${fmtDate(c.coe_date)}${days != null && days < 0 ? ' - needs an extension' : ''}` : 'No close date set'}
        />
        <Kpi label="Checklist complete" value={`${c.kpis.percent_complete}%`} bar={c.kpis.percent_complete} />
      </div>

      {/* Escrow timeline */}
      <Card className="px-[22px] pb-6 pt-[18px]">
        <div className="mb-[22px] flex flex-wrap items-center gap-2.5">
          <h2 className={h2}>Escrow timeline</h2>
          <span className="text-[11.5px] text-gray-400">
            {c.deal.escrow_opened_date ? `Opened ${fmtDate(c.deal.escrow_opened_date)}` : 'Not opened yet'}
            {c.coe_date ? ` -> COE ${fmtDate(c.coe_date)}` : ''}
          </span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {na && (
              <span className={`rounded-full px-[9px] py-[5px] text-[11px] font-semibold leading-none ${
                na.days_until != null && na.days_until < 0 ? 'bg-primary-100 text-primary-500' : 'bg-gray-100 text-gray-700'
              }`}>
                Next: {na.label}
                {na.days_until != null ? ` · ${na.days_until < 0 ? `${Math.abs(na.days_until)}d overdue` : `${na.days_until}d`}` : ''}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={() => setAskAi(true)}>Ask AI</Button>
          </div>
        </div>
        {tl ? (() => {
          const sorted = [...tl.nodes].sort((a, b) => a.pct - b.pct);
          // The live milestone is always labelled. The rest get a label only where there is room:
          // 14% of the track from the ends, from each other and from the live one. Real escrows
          // cluster deadlines within days, and labelling every dot printed them on top of each
          // other (looked at on the Aguilera deal, 2026-09-03). Every dot still carries its title.
          const livePct = sorted.find((n) => n.next)?.pct;
          let lastLabelled = -100;
          const labelled = sorted.map((n) => {
            if (n.next) { lastLabelled = n.pct; return true; }
            const room = n.pct >= 14 && n.pct <= 86 && n.pct - lastLabelled >= 14 && (livePct == null || Math.abs(n.pct - livePct) >= 14);
            if (room) lastLabelled = n.pct;
            return room;
          });
          const endDone = tl.today >= 100;
          // A live milestone at either end (an overdue one past the COE clamps to 100%) takes
          // that end's label slot and aligns into the track; the Opened / COE captions yield.
          const liveAtEnd = livePct != null && livePct > 86;
          const liveAtStart = livePct != null && livePct < 14;
          const edgeOf = (n: { pct: number; next: boolean }) => (n.next && liveAtEnd ? 'end' : n.next && liveAtStart ? 'start' : 'mid');
          return (
            <div className="relative mx-1 h-14">
              <span className="absolute left-0 right-0 top-[9px] h-0.5 bg-gray-200" />
              <span className="absolute left-0 top-[9px] h-0.5 bg-gray-900" style={{ width: `${tl.today}%` }} />
              <span className="absolute top-[3px] h-[14px] w-0.5 bg-gray-900" style={{ left: `${tl.today}%`, transform: 'translateX(-50%)' }} title="Today" />
              <div className="absolute left-0 top-0 flex flex-col items-start gap-[9px]">
                <span className="mt-1 block h-[11px] w-[11px] rounded-full bg-gray-900" />
                {!liveAtStart && <span className="text-[10.5px] font-medium leading-tight text-gray-700">Opened<br /><span className="text-gray-400">{fmtDate(c.deal.escrow_opened_date)}</span></span>}
              </div>
              <div className="absolute right-0 top-0 flex flex-col items-end gap-[9px]">
                <span className={`mt-1 block h-[11px] w-[11px] rounded-full ${endDone ? 'bg-gray-900' : 'border-2 border-gray-300 bg-white'}`} />
                {!liveAtEnd && <span className="text-right text-[10.5px] font-medium leading-tight text-gray-700">Close of escrow<br /><span className="text-gray-400">{fmtDate(c.coe_date)}</span></span>}
              </div>
              {sorted.map((n, i) => {
                const edge = edgeOf(n);
                return (
                  <div
                    key={i}
                    title={`${n.label} · ${fmtDate(n.date)}${n.status === 'overdue' ? ' · overdue' : ''}`}
                    className={`absolute top-0 flex flex-col gap-[9px] ${edge === 'end' ? 'items-end' : edge === 'start' ? 'items-start' : 'items-center'}`}
                    style={edge === 'end' ? { right: 0 } : edge === 'start' ? { left: 0 } : { left: `${n.pct}%`, transform: 'translateX(-50%)' }}
                  >
                    <span className={`mt-1 block h-[11px] w-[11px] rounded-full ${NODE_CLS[n.status] || NODE_CLS.upcoming}`} />
                    {labelled[i] && (
                      <span className={`max-w-[190px] truncate text-[10.5px] leading-tight ${edge === 'end' ? 'text-right' : edge === 'start' ? 'text-left' : 'text-center'} ${n.next ? 'font-bold text-primary-500' : 'font-medium text-gray-700'}`}>
                        {n.label}<br /><span className={n.next ? 'font-normal' : 'text-gray-400'}>{fmtDate(n.date)}{n.status === 'overdue' ? ' · overdue' : ''}</span>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })() : (
          <p className="text-[12.5px] text-gray-500">Set the escrow opened date and the close of escrow on the Deadlines tab to draw the timeline.</p>
        )}
      </Card>

      {/* Parties beside Money + Required documents + Tasks */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
        {/* LF2: Card is overflow-hidden - it cut off a party's Call callout under the LAST row when no Contingencies
            follow. This card lets it out (the important suffix beats Card's own class). */}
        <Card className="overflow-visible!">
          <div className="flex items-center border-b border-gray-150 px-5 py-4">
            <h2 className={h2}>Parties</h2>
          </div>
          {sortedParties.length === 0 && <p className="px-5 py-4 text-[12.5px] text-gray-500">No parties linked.</p>}
          {/* LF2: said ONCE here - inline in each row it pushed every party's buttons over the names on a phone. */}
          {sortedParties.some((p) => p.ghl_link_unread) && <p role="status" className="px-5 pt-3 text-xs text-gray-500">{UNREAD}</p>}
          {sortedParties.map((p, i) => (
            <div key={partyKey(p, i)} className="flex items-center gap-3 border-b border-gray-100 px-5 py-3.5 last:border-b-0">
              <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg bg-gray-100 text-[11px] font-bold text-gray-700">{initialsOf(p.name)}</div>
              <div className="min-w-0 flex-1">
                <div className="mb-[5px] text-[10px] uppercase leading-none tracking-[.13em] text-gray-400">{ROLE_LABEL[p.role] || p.role}</div>
                {p.contact_id
                  ? <button type="button" onClick={() => navigate(`/crm/contacts/${p.contact_id}`)} className="block max-w-full truncate text-left text-[13.5px] font-semibold leading-tight text-gray-800 hover:text-primary-500">{p.name || '—'}</button>
                  : <p className="truncate text-[13.5px] font-semibold leading-tight text-gray-800">{p.name || '—'}</p>}
                <p className="truncate text-[11.5px] leading-snug text-gray-500">{[p.email, p.phone].filter(Boolean).join(' · ') || 'No contact info'}</p>
              </div>
              <div className="flex shrink-0 gap-1.5">
                <CallInGhl variant="icon" url={p.ghl_url} contactId={p.contact_id} canAdd={!!p.can_add_to_ghl} unread={p.ghl_link_unread} />
                <button type="button" onClick={() => setNudge({ party: p, channel: 'email' })} disabled={!p.contact_id || !p.email} title={p.email ? 'Email' : 'No email'} aria-label="Email" className={iconBtn}><svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2.5" y="4.5" width="19" height="15" rx="2" /><polyline points="3 6 12 13 21 6" /></svg></button>
                <button type="button" onClick={() => setNudge({ party: p, channel: 'sms' })} disabled={!p.contact_id || !p.phone} title={p.phone ? 'Text' : 'No phone'} aria-label="Text" className={iconBtn}><svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a8 8 0 01-8 8H8l-5 3 1.5-4.5A8 8 0 1121 12z" /></svg></button>
              </div>
            </div>
          ))}
          {c.contingencies.length > 0 && (
            <div className="border-t border-gray-150 px-5 py-4">
              <div className="mb-2.5 text-[10px] uppercase leading-none tracking-[.13em] text-gray-400">Contingencies</div>
              <div className="flex flex-wrap gap-[7px]">
                {c.contingencies.map((ct) => (
                  <Badge key={ct.type} variant={contingencyVariant(ct.status)}>
                    {ct.label} {ct.status === 'removed' ? 'cleared' : ct.status === 'overdue' ? `${Math.abs(ct.days_until ?? 0)}d overdue` : ct.days_until != null ? `due ${ct.days_until}d` : 'active'}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </Card>

        <div className="flex flex-col gap-4">
          <Card>
            <div className="flex items-center border-b border-gray-150 px-5 py-4">
              <h2 className={h2}>Money</h2>
              {!editMoney && (
                <button type="button" onClick={() => { setEmd(c.money.emd?.toString() || ''); setLoan(c.money.loan?.toString() || ''); setEditMoney(true); }} className={`ml-auto ${linkCls}`}>Edit terms</button>
              )}
            </div>
            <div className="px-5 pb-3.5 pt-1.5">
              {[
                ['Purchase price', fmtMoney(c.money.price), null],
                ['Earnest money deposit', fmtMoney(c.money.emd), 'emd'],
                [`Loan amount${c.money.ltv != null ? ` (${c.money.ltv}% LTV)` : ''}`, fmtMoney(c.money.loan), 'loan'],
                ['Gross commission', fmtMoney(c.money.gross_commission), null],
              ].map(([label, value, field]) => (
                <div key={label as string} className="flex items-center justify-between gap-3 border-b border-gray-100 py-[11px] text-[13px] font-medium leading-none text-gray-700">
                  <span>{label}</span>
                  {editMoney && field ? (
                    <input
                      type="number"
                      value={field === 'emd' ? emd : loan}
                      onChange={(e) => (field === 'emd' ? setEmd(e.target.value) : setLoan(e.target.value))}
                      className="w-32 rounded-lg border-gray-300 py-1 text-right text-xs"
                      aria-label={label as string}
                    />
                  ) : (
                    <span className="font-semibold tabular-nums text-gray-800">{value as string}</span>
                  )}
                </div>
              ))}
              <div className="flex items-center justify-between pb-1 pt-[13px] text-[13.5px] font-semibold leading-none text-gray-900">
                <span>Your split</span><span className="tabular-nums">{fmtMoney(c.money.your_split)}</span>
              </div>
              {editMoney && (
                <div className="mt-2 flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setEditMoney(false)}>Cancel</Button>
                  <Button size="sm" onClick={saveMoney}>Save</Button>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="flex items-center border-b border-gray-150 px-5 py-4">
              <h2 className={h2}>Required documents</h2>
              <span className="ml-auto text-[11.5px] font-semibold text-gray-500">{presentDocs} of {c.documents.required.length}</span>
            </div>
            <div className="px-5 pb-4 pt-2">
              {c.documents.required.map((d) => (
                <div key={d.key} className="flex items-center gap-2.5 border-b border-gray-100 py-2.5 last:border-b-0">
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm text-[9px] font-bold text-white ${d.present ? 'bg-success' : 'border-[1.5px] border-gray-300'}`}>{d.present ? '✓' : ''}</span>
                  <span className={`text-[13px] font-medium leading-none ${d.present ? 'text-gray-800' : 'text-gray-700'}`}>{d.label}</span>
                  <span className={`ml-auto text-[11px] leading-none ${d.present ? 'text-gray-400' : 'font-semibold text-warning'}`}>{d.present ? 'On file' : 'Missing'}</span>
                </div>
              ))}
              {c.documents.other_count > 0 && (
                <p className="pt-2.5 text-[11px] text-gray-400">{c.documents.other_count} other file{c.documents.other_count === 1 ? '' : 's'} in the Deal Room.</p>
              )}
            </div>
          </Card>

          <Card>
            <div className="flex items-center border-b border-gray-150 px-5 py-4">
              <h2 className={h2}>Tasks</h2>
              <span className="ml-2 text-[11.5px] text-gray-400">not in the checklist</span>
              {!adding && <button type="button" onClick={() => setAdding(true)} className={`ml-auto ${linkCls}`}>+ Add task</button>}
            </div>
            <div className="px-5 pb-4 pt-2">
              {tasks.length === 0 && !adding && <p className="py-1 text-[12.5px] text-gray-500">None yet.</p>}
              {tasks.map((t) => (
                <label key={t.id} className="flex cursor-pointer items-center gap-2.5 border-b border-gray-100 py-2.5 last:border-b-0">
                  <input type="checkbox" checked={t.is_completed} onChange={() => toggleTask(t)} className="h-4 w-4 rounded-sm border-gray-300 text-gray-900 focus:ring-primary-500/25" />
                  <span className={`flex-1 text-[13px] leading-none ${t.is_completed ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{t.title}</span>
                  {t.due_date && <span className="text-[11px] tabular-nums text-gray-400">{fmtDate(t.due_date)}</span>}
                </label>
              ))}
              {adding && (
                <div className="mt-2 flex items-center gap-2">
                  <input autoFocus value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addTask()} placeholder="Task title..." className="flex-1 rounded-lg border-gray-300 text-[13px]" />
                  <Button size="sm" onClick={addTask}>Add</Button>
                  <Button variant="ghost" size="sm" onClick={() => { setAdding(false); setNewTitle(''); }}>Cancel</Button>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {nudge && <NudgeComposer dealId={dealId} party={nudge.party} channel={nudge.channel} nextLabel={nextM?.label ?? null} nextDate={nextM?.due_date ?? null} onClose={() => setNudge(null)} />}
      {askAi && <AskAiPanel dealId={dealId} dealName={c.deal.deal_name} parties={c.parties} onClose={() => setAskAi(false)} onChanged={load} />}
    </div>
  );
}
